import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Loader2, Bell, Check } from 'lucide-react';
import { activityApi, GroupedActivity, ActivityType, EntityType } from '@/lib/api';
import { Avatar } from '@/components/ui/Avatar';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { cn, formatRelativeTime } from '@/lib/utils';
import { ACTIVITY_DISPLAY, activityGroupLabel, activityLink } from '@/lib/activity-display';

// Filter tabs
const filterTabs: { label: string; value: ActivityType | 'all' }[] = [
  { label: 'All', value: 'all' },
  { label: 'Mentions', value: 'MENTION' },
  { label: 'Replies', value: 'REPLY' },
  { label: 'Tasks', value: 'TASK_ASSIGNED' },
];

// Group activities by date
function groupActivitiesByDate(activities: GroupedActivity[]): Record<string, GroupedActivity[]> {
  const groups: Record<string, GroupedActivity[]> = {};
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const weekAgo = new Date(today);
  weekAgo.setDate(weekAgo.getDate() - 7);

  for (const activity of activities) {
    const date = new Date(activity.createdAt);
    let key: string;

    if (date.toDateString() === today.toDateString()) {
      key = 'Today';
    } else if (date.toDateString() === yesterday.toDateString()) {
      key = 'Yesterday';
    } else if (date > weekAgo) {
      key = 'Earlier This Week';
    } else {
      key = 'Older';
    }

    if (!groups[key]) {
      groups[key] = [];
    }
    groups[key].push(activity);
  }

  return groups;
}

interface ActivityFeedCardProps {
  userId?: string;
  onUnreadCountChange?: (count: number) => void;
}

export function ActivityFeedCard({ userId, onUnreadCountChange }: ActivityFeedCardProps) {
  const queryClient = useQueryClient();
  const [activeFilter, setActiveFilter] = useState<ActivityType | 'all'>('all');
  const [showRead, setShowRead] = useState(false);
  const [page, setPage] = useState(1);

  // Fetch grouped activities (unread only by default)
  const { data, isLoading } = useQuery({
    queryKey: ['activities', userId, activeFilter, page, 'grouped', showRead],
    queryFn: () => activityApi.list({
      page,
      limit: 20,
      type: activeFilter === 'all' ? undefined : activeFilter,
      unreadOnly: !showRead,
      grouped: true,
    }),
    enabled: !!userId,
  });

  // Update parent with unread count
  const unreadCount = data?.unreadCount || 0;
  useEffect(() => {
    if (onUnreadCountChange && data) {
      onUnreadCountChange(unreadCount);
    }
  }, [unreadCount, data, onUnreadCountChange]);

  // Mark single as read mutation
  const markReadMutation = useMutation({
    mutationFn: (id: string) => activityApi.markAsRead(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['activities'] });
    },
  });

  // Mark group as read mutation
  const markGroupReadMutation = useMutation({
    mutationFn: (params: { entityType?: EntityType | null; entityId?: string | null; activityType: ActivityType }) =>
      activityApi.markGroupAsRead(params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['activities'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-action-items'] });
    },
  });

  // Mark all as read mutation
  const markAllReadMutation = useMutation({
    mutationFn: () => activityApi.markAllAsRead(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['activities'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-action-items'] });
    },
  });

  const activities = data?.items || [];
  const groupedActivities = groupActivitiesByDate(activities);
  const dateGroups = ['Today', 'Yesterday', 'Earlier This Week', 'Older'];

  return (
    <Card padding="none" className="h-full flex flex-col">
      {/* Header with filter tabs */}
      <div className="px-4 py-3 border-b border-grey-100 bg-grey-50/50">
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm font-medium text-secondary">Recent Activity</span>
          <div className="flex items-center gap-3">
            <button
              onClick={() => { setShowRead(!showRead); setPage(1); }}
              className={cn(
                'text-xs transition-colors',
                showRead ? 'text-accent font-medium' : 'text-grey-400 hover:text-grey-600'
              )}
            >
              {showRead ? 'Show unread' : 'Show all'}
            </button>
            {unreadCount > 0 && (
              <>
                <span className="text-xs text-grey-300">·</span>
                <button
                  onClick={() => markAllReadMutation.mutate()}
                  disabled={markAllReadMutation.isPending}
                  className="flex items-center gap-1 text-xs text-accent hover:text-accent/80 disabled:opacity-50 transition-colors"
                >
                  <Check className="w-3 h-3" />
                  Mark All Read
                </button>
              </>
            )}
          </div>
        </div>
        <div className="flex gap-2 overflow-x-auto pb-1">
          {filterTabs.map((tab) => (
            <button
              key={tab.value}
              onClick={() => {
                setActiveFilter(tab.value);
                setPage(1);
              }}
              className={cn(
                'px-3 py-1.5 text-xs font-medium rounded-full transition-colors whitespace-nowrap',
                activeFilter === tab.value
                  ? 'bg-secondary text-white'
                  : 'bg-grey-100 text-grey-600 hover:bg-grey-200'
              )}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Loading state */}
      {isLoading && (
        <div className="flex-1 flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 text-grey-400 animate-spin" />
        </div>
      )}

      {/* Empty state */}
      {!isLoading && activities.length === 0 && (
        <div className="flex-1 flex flex-col items-center justify-center py-12 text-center px-4">
          <div className="w-12 h-12 rounded-full bg-grey-100 flex items-center justify-center mb-3">
            {!showRead ? (
              <Check className="w-6 h-6 text-emerald-500" />
            ) : (
              <Bell className="w-6 h-6 text-grey-400" />
            )}
          </div>
          <p className="text-sm text-grey-500">
            {!showRead ? "You're all caught up!" : 'No activity yet'}
          </p>
          <p className="text-xs text-grey-400 mt-1">
            {!showRead
              ? 'No unread notifications right now.'
              : 'Updates will appear here when things happen'}
          </p>
        </div>
      )}

      {/* Activity list */}
      {!isLoading && activities.length > 0 && (
        <div className="flex-1 overflow-y-auto">
          {dateGroups.map((dateKey) => {
            const groupActivities = groupedActivities[dateKey];
            if (!groupActivities || groupActivities.length === 0) return null;

            return (
              <div key={dateKey}>
                <h3 className="text-xs font-semibold text-grey-500 uppercase tracking-wider px-4 py-2 bg-grey-50/50 sticky top-0">
                  {dateKey}
                </h3>
                <div className="divide-y divide-grey-100">
                  {groupActivities.map((activity) => {
                    const config = ACTIVITY_DISPLAY[activity.type];
                    const Icon = config.icon;
                    const link = activityLink(activity);
                    const hasMultiple = activity.count > 1;
                    const isUnread = activity.hasUnread || !activity.isRead;

                    return (
                      <Link
                        key={`${activity.entityType}-${activity.entityId}-${activity.type}`}
                        to={link}
                        onClick={() => {
                          if (isUnread) {
                            if (hasMultiple) {
                              markGroupReadMutation.mutate({
                                entityType: activity.entityType as EntityType | undefined,
                                entityId: activity.entityId,
                                activityType: activity.type,
                              });
                            } else {
                              markReadMutation.mutate(activity.id);
                            }
                          }
                        }}
                        className="flex items-start gap-3 p-4 hover:bg-grey-50 transition-colors"
                      >
                        {/* Unread indicator */}
                        <div className="w-2 shrink-0 pt-2">
                          {isUnread && (
                            <div className="w-2 h-2 rounded-full bg-primary" />
                          )}
                        </div>

                        {/* Icon with count badge */}
                        <div className="relative shrink-0">
                          <div className={cn(
                            'w-8 h-8 rounded-full flex items-center justify-center',
                            config.bgColor
                          )}>
                            <Icon className={cn('w-4 h-4', config.color)} />
                          </div>
                          {hasMultiple && (
                            <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 bg-primary text-white text-[10px] font-medium rounded-full flex items-center justify-center">
                              {activity.count}
                            </span>
                          )}
                        </div>

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-secondary line-clamp-1">
                            {hasMultiple ? activityGroupLabel(activity.type, activity.count) : activity.title}
                          </p>
                          <p className="text-xs text-grey-500 line-clamp-1 mt-0.5">
                            {hasMultiple ? `in "${activity.description || activity.title}"` : activity.description}
                          </p>
                          <div className="flex items-center gap-2 mt-1 flex-wrap">
                            {activity.project && (
                              <span className="text-xs text-grey-400">{activity.project.name}</span>
                            )}
                            {activity.project?.organization && (
                              <span className="badge-accent min-w-0 max-w-[45vw] truncate">{activity.project.organization.name}</span>
                            )}
                            {activity.project && (
                              <span className="text-xs text-grey-300">·</span>
                            )}
                            <span className="text-xs text-grey-400">
                              {formatRelativeTime(new Date(activity.createdAt))}
                            </span>
                          </div>
                        </div>

                        {/* Actor avatars */}
                        <div className="flex -space-x-1.5 shrink-0">
                          {hasMultiple && activity.actors ? (
                            <>
                              {activity.actors.slice(0, 2).map((actor) => (
                                <Avatar
                                  key={actor.id}
                                  src={actor.avatarUrl}
                                  name={actor.name}
                                  size="xs"
                                  className="ring-2 ring-white"
                                />
                              ))}
                              {activity.actors.length > 2 && (
                                <div className="w-6 h-6 rounded-full bg-grey-200 flex items-center justify-center text-[10px] font-medium text-grey-600 ring-2 ring-white">
                                  +{activity.actors.length - 2}
                                </div>
                              )}
                            </>
                          ) : activity.actor ? (
                            <Avatar
                              src={activity.actor.avatarUrl}
                              name={activity.actor.name}
                              size="sm"
                            />
                          ) : null}
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </div>
            );
          })}

          {/* Load more */}
          {data?.pagination.hasMore && (
            <div className="p-4 border-t border-grey-100">
              <Button
                variant="ghost"
                size="sm"
                className="w-full"
                onClick={() => setPage((p) => p + 1)}
              >
                Load More
              </Button>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}
